import React, { useState, useRef, useEffect } from "react";
import { LogIn, Settings } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

interface LoginMenuProps {
  isDarkMode: boolean;
  onMenuStateChange?: (isOpen: boolean, closeFn?: () => void) => void;
  onOpenSettings: () => void;
}

const LoginMenu: React.FC<LoginMenuProps> = ({ isDarkMode, onMenuStateChange, onOpenSettings }) => {
  const { signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && buttonRef.current && !menuRef.current.contains(event.target as Node) && !buttonRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Notify parent when menu state changes and provide close function
  useEffect(() => {
    onMenuStateChange?.(isMenuOpen, () => setIsMenuOpen(false));
  }, [isMenuOpen, onMenuStateChange]);

  const handleSignInWithGoogle = async () => {
    setIsLoading(true);
    setIsMenuOpen(false); // Close menu when signing in
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error("Failed to sign in with Google:", error);
      alert("Google Sign-In not available. Please check your Firebase configuration.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsMenuOpen(false);
  };

  return (
    <div className="relative">
      {/* Sign In Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        title="Sign in options"
        aria-label="Open sign in menu"
      >
        <LogIn size={14} />
        <span className="hidden sm:inline">Sign in</span>
      </button>

      {/* Sign In Options Dropdown */}
      {isMenuOpen && (
        <div
          ref={menuRef}
          className={`absolute w-64 py-2 overflow-hidden
            ${/* Align with navbar edge - right edge with padding */ ""}
            -right-4 md:-right-4
            ${/* Add gap between navbar and dropdown */ ""}
            md:mt-6 mb-6 md:mb-0
            ${/* Match navbar styling exactly - semi-transparent borders */ ""}
            ${isDarkMode ? "bg-gray-800 border-gray-700/20" : "bg-white border-white/20"} 
            shadow-lg rounded-2xl border
            ${/* Mobile positioning - above navbar with gap */ ""}
            bottom-full md:bottom-auto md:top-full
            ${/* Higher z-index to appear above all modals including list view */ ""}
            z-[300]
          `}
        >
          {/* Header */}
          <div className={`px-4 py-3 border-b ${isDarkMode ? "border-gray-700/20" : "border-gray-200"}`}>
            <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-black"}`}>Sign in to your account</p>
            <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Choose your preferred sign-in method</p>
          </div>

          {/* Sign In Options */}
          <div className="py-1">
            {/* Google Sign In */}
            <button
              onClick={() => handleMenuItemClick(handleSignInWithGoogle)}
              disabled={isLoading}
              className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-black hover:bg-gray-50"
                }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </div>
              Continue with Google
            </button>

            {/* Future sign-in options can be added here */}

            {/* Divider */}
            <div className={`border-t ${isDarkMode ? "border-gray-700/20" : "border-gray-200"} my-2 mx-4`}></div>

            {/* Settings - Available to all users */}
            <button
              onClick={() => handleMenuItemClick(onOpenSettings)}
              className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-black hover:bg-gray-50"
                }`}
            >
              <Settings size={16} />
              Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginMenu;
